
import React from 'react';
import { createPortal } from 'react-dom';
import { X, Weight, Watch, Smartphone, Bluetooth, HeartPulse, Activity, Footprints } from 'lucide-react';
import { t } from '../locales';
import { Language } from '../types';

interface HealthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenScaleMeasure: () => void;
  onOpenActivity: () => void;
  language: Language;
}

const HealthModal: React.FC<HealthModalProps> = ({ 
    isOpen, onClose, onOpenScaleMeasure, onOpenActivity, language 
}) => {
  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-gray-50 dark:bg-gray-900 animate-slideIn">
      <div className="w-full h-full max-w-md mx-auto flex flex-col bg-white dark:bg-gray-900 shadow-xl overflow-hidden">
        
        {/* Header */}
        <div className="px-4 py-4 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center bg-white/80 dark:bg-gray-900/80 backdrop-blur-md sticky top-0 z-10 pt-safe">
            <h2 className="text-xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
                <HeartPulse className="text-rose-500" size={24} />
                {t(language, 'healthRecords')}
            </h2>
            <button 
                onClick={onClose}
                className="p-2 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
                <X size={24} />
            </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-6">
            
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 leading-relaxed">
                {t(language, 'healthRecordsDesc')}
            </p>

            {/* Active Tools Section */}
            <div className="mb-8">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                    <Activity size={14} /> {t(language, 'activeTools')}
                </h3>
                
                <div className="grid grid-cols-1 gap-4">
                    {/* Activity Tracking Tool */}
                    <button 
                        onClick={onOpenActivity}
                        className="flex items-center gap-4 p-4 bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-800 rounded-2xl hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-all active:scale-[0.98] group shadow-sm"
                    >
                        <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-800 text-blue-600 dark:text-blue-200 flex items-center justify-center group-hover:scale-110 transition-transform">
                            <Footprints size={24} />
                        </div>
                        <div className="flex-1 text-left">
                            <h4 className="font-bold text-gray-800 dark:text-white text-base">{t(language, 'sportActivity')}</h4>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{t(language, 'logExercise')}</p>
                        </div>
                        <div className="w-8 h-8 rounded-full bg-white dark:bg-gray-900 flex items-center justify-center text-blue-500 shadow-sm">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" /></svg>
                        </div>
                    </button>

                    {/* Scale Measure Tool */}
                    <button 
                        onClick={onOpenScaleMeasure}
                        className="flex items-center gap-4 p-4 bg-indigo-50 dark:bg-indigo-900/10 border border-indigo-100 dark:border-indigo-800 rounded-2xl hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-all active:scale-[0.98] group shadow-sm"
                    >
                        <div className="w-12 h-12 rounded-xl bg-indigo-100 dark:bg-indigo-800 text-indigo-600 dark:text-indigo-200 flex items-center justify-center group-hover:scale-110 transition-transform">
                            <Weight size={24} />
                        </div>
                        <div className="flex-1 text-left">
                            <h4 className="font-bold text-gray-800 dark:text-white text-base">{t(language, 'scaleMeasure')}</h4>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{t(language, 'logWeight')}</p>
                        </div>
                        <div className="w-8 h-8 rounded-full bg-white dark:bg-gray-900 flex items-center justify-center text-indigo-500 shadow-sm">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" /></svg>
                        </div>
                    </button>
                </div>
            </div>

            {/* Smart Devices Section (Placeholders) */}
            <div>
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                    <Bluetooth size={14} /> {t(language, 'connectDevices')}
                </h3>
                
                <div className="grid grid-cols-2 gap-3">
                    {/* Smart Watch Placeholder */}
                    <button disabled className="flex flex-col items-center justify-center p-4 bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-800 rounded-2xl opacity-60 cursor-not-allowed">
                        <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-400 flex items-center justify-center mb-3">
                            <Watch size={20} />
                        </div>
                        <span className="font-bold text-xs text-gray-600 dark:text-gray-400">{t(language, 'smartWatch')}</span>
                        <span className="text-[9px] text-gray-400 mt-1 uppercase tracking-wide font-bold bg-gray-200 dark:bg-gray-700 px-2 py-0.5 rounded">{t(language, 'soon')}</span>
                    </button>

                    {/* Smart Scale Placeholder */}
                    <button disabled className="flex flex-col items-center justify-center p-4 bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-800 rounded-2xl opacity-60 cursor-not-allowed">
                        <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-400 flex items-center justify-center mb-3">
                            <Smartphone size={20} />
                        </div>
                        <span className="font-bold text-xs text-gray-600 dark:text-gray-400">{t(language, 'smartScale')}</span>
                        <span className="text-[9px] text-gray-400 mt-1 uppercase tracking-wide font-bold bg-gray-200 dark:bg-gray-700 px-2 py-0.5 rounded">{t(language, 'soon')}</span>
                    </button>
                </div>
            </div>

        </div>
      </div>
    </div>,
    document.body
  );
};

export default HealthModal;
